import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import { fetchBrands as loadBrands, fetchCategories as loadCategories } from '../services/sellerCatalog';
import { Upload, X, ArrowLeft, Image as ImageIcon } from 'lucide-react';
import { compressImage, validateImageFile } from '../utils/imageCompression';
import ProductVariantEditor, {
  VariantConfig,
  VariantRow,
} from '../components/products/ProductVariantEditor';
import { productHasVariants } from '../utils/productVariants';

interface Category {
  id: string;
  name: string;
  parent?: string | null;
  isActive?: boolean;
}

interface Brand {
  id: string;
  name: string;
  isActive?: boolean;
}


const AddProduct: React.FC = () => {
  const { t } = useTranslation('addProduct');
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;
  const [loading, setLoading] = useState(false);
  const [loadingProduct, setLoadingProduct] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    brand: '',
    price: '',
    originalPrice: '',
    quantityAvailable: '',
    productType: 'simple' as 'simple' | 'variable',
    condition: 'Brand New' as 'Brand New' | 'Used' | 'Refurbished',
    color: '',
    isActive: true,
    images: [] as string[],
  });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [variantConfig, setVariantConfig] = useState<VariantConfig>({
    hasSizes: false,
    hasColors: false,
    sizes: [],
    colors: [],
  });
  const [variantRows, setVariantRows] = useState<VariantRow[]>([]);

  const applyProductToForm = (product: Record<string, unknown>) => {
    const isVariable = productHasVariants({
      productType: product.productType as string | undefined,
      variants: product.variants as unknown[] | undefined,
      variantConfig: product.variantConfig as VariantConfig | undefined,
    });

    setFormData({
      name: String(product.name || ''),
      description: String(product.description || ''),
      category:
        (product.category as { id?: string; _id?: string })?.id ||
        (product.category as { id?: string; _id?: string })?._id ||
        String(product.category || ''),
      brand: String(product.brand || ''),
      price:
        product.price != null
          ? String(product.price)
          : (product.priceRange as { min?: number })?.min?.toString() || '',
      originalPrice:
        product.originalPrice != null
          ? String(product.originalPrice)
          : (product.priceRange as { max?: number })?.max?.toString() || '',
      quantityAvailable:
        product.quantityAvailable != null ? String(product.quantityAvailable) : '',
      productType: isVariable ? 'variable' : 'simple',
      condition: (product.condition as 'Brand New' | 'Used' | 'Refurbished') || 'Brand New',
      color: String(product.color || ''),
      isActive: product.isActive !== undefined ? Boolean(product.isActive) : true,
      images: (product.images as string[]) || [],
    });

    if (isVariable) {
      const cfg =
        (product.variantConfig as VariantConfig) || {
          hasSizes: ((product.availableSizes as string[]) || []).length > 0,
          hasColors: ((product.availableColors as string[]) || []).length > 0,
          sizes: (product.availableSizes as string[]) || [],
          colors: (product.availableColors as string[]) || [],
        };
      setVariantConfig({
        hasSizes: cfg.hasSizes ?? cfg.sizes.length > 0,
        hasColors: cfg.hasColors ?? cfg.colors.length > 0,
        sizes: cfg.sizes || [],
        colors: cfg.colors || [],
      });
      setVariantRows(
        ((product.variants as Array<Record<string, unknown>>) || []).map((v) => ({
          id: (v.id || v._id) as string | undefined,
          size: v.size as string | undefined,
          color: v.color as string | undefined,
          price: Number(v.price ?? 0),
          originalPrice: Number(v.originalPrice ?? v.price ?? 0),
          quantityAvailable: Number(v.quantityAvailable ?? 0),
          sku: v.sku as string | undefined,
        }))
      );
    } else {
      setVariantConfig({ hasSizes: false, hasColors: false, sizes: [], colors: [] });
      setVariantRows([]);
    }
  };

  const enableVariableProduct = () => {
    setFormData((prev) => ({ ...prev, productType: 'variable' }));
    setVariantConfig((prev) => ({
      hasSizes: true,
      hasColors: true,
      sizes: prev.sizes,
      colors: prev.colors,
    }));
  };

  const handleProductTypeChange = (nextType: 'simple' | 'variable') => {
    setFormData({ ...formData, productType: nextType });
    if (nextType === 'variable' && !variantConfig.hasSizes && !variantConfig.hasColors) {
      setVariantConfig({ hasSizes: true, hasColors: true, sizes: [], colors: [] });
    }
  };

  const fetchProduct = useCallback(
    async (productId: string) => {
      setLoadingProduct(true);
      try {
        const response = await api.get(`/seller/products/${productId}`);
        const product = response.data.data;
        if (!product) {
          alert('Product not found');
          navigate('/products');
          return;
        }
        applyProductToForm(product);
      } catch (error) {
        console.error('Failed to fetch product:', error);
        alert('Failed to load product');
        navigate('/products');
      } finally {
        setLoadingProduct(false);
      }
    },
    [navigate]
  );

  useEffect(() => {
    fetchCategories();
    fetchBrands();
    if (isEditMode && id) {
      fetchProduct(id);
    }
  }, [id, isEditMode, fetchProduct]);

  const fetchCategories = async () => {
    try {
      const list = await loadCategories<Category>();
      setCategories(list.filter((c) => c.isActive !== false));
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchBrands = async () => {
    try {
      const list = await loadBrands<Brand>();
      setBrands(list.filter((b) => b.isActive !== false));
    } catch (error) {
      console.error('Failed to fetch brands:', error);
    }
  };

  // Helper function to get categories for product selection (only leaf categories)
  const getSelectableCategories = () => {
    const allCategories = categories;
    const categoriesWithChildren = new Set(
      allCategories.filter(cat => cat.parent).map(cat => cat.parent)
    );
    return allCategories.filter(cat => !categoriesWithChildren.has(cat.id));
  };

  // Helper function to format category name with parent hierarchy
  const formatCategoryName = (category: Category) => {
    const parent = categories.find(cat => cat.id === category.parent);
    return parent ? `${parent.name} > ${category.name}` : category.name;
  };

  const handleImageUpload = async (file: File) => {
    if (!file) return;

    // Validate file
    const validation = validateImageFile(file);
    if (!validation.valid) {
      alert(validation.error);
      return;
    }

    setUploadingImage(true);
    try {
      // Compress image before upload to reduce file size and upload time
      const compressedFile = await compressImage(file, 1920, 0.85);
      console.log(`Image compressed: ${(file.size / 1024 / 1024).toFixed(2)}MB -> ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB`);

      const uploadFormData = new FormData();
      uploadFormData.append('image', compressedFile);

      const response = await api.post('/upload-single-file', uploadFormData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.data?.fileUrl) {
        setFormData(prev => ({
          ...prev,
          images: [...prev.images, response.data.data.fileUrl]
        }));
      } else {
        throw new Error('Invalid upload response');
      }
    } catch (error: any) {
      console.error('Failed to upload image:', error);
      alert(error.response?.data?.message || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleImageRemove = (index: number) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageUpload(file);
    }
    // Reset input to allow selecting the same file again
    e.target.value = '';
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleImageUpload(e.dataTransfer.files[0]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name.trim() || !formData.description.trim() || !formData.brand || !formData.category) {
      alert('Please fill in all required fields');
      return;
    }

    if (formData.images.length === 0) {
      alert('Please upload at least one product image');
      return;
    }

    if (formData.productType === 'variable') {
      if (!variantConfig.hasSizes && !variantConfig.hasColors) {
        alert(t('variants.validationOptions'));
        return;
      }
      if (variantConfig.hasSizes && variantConfig.sizes.length === 0) {
        alert(t('variants.validationSizes'));
        return;
      }
      if (variantConfig.hasColors && variantConfig.colors.length === 0) {
        alert(t('variants.validationColors'));
        return;
      }
    }

    setLoading(true);
    try {
      const basePrice = parseFloat(formData.price) || 0;
      const baseOriginalPrice = formData.originalPrice
        ? parseFloat(formData.originalPrice)
        : basePrice;

      let payload: Record<string, unknown> = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        brand: formData.brand.trim(),
        category: formData.category,
        productType: formData.productType,
        images: formData.images,
        isActive: formData.isActive,
      };

      if (formData.productType === 'variable') {
        payload = {
          ...payload,
          variantConfig,
          variants: variantRows,
          basePrice,
          baseOriginalPrice,
        };
      } else {
        payload = {
          ...payload,
          price: basePrice,
          originalPrice: baseOriginalPrice,
          quantityAvailable: parseInt(formData.quantityAvailable, 10) || 0,
          condition: formData.condition,
          color: formData.color.trim() || undefined,
        };
      }

      if (isEditMode && id) {
        await api.put(`/seller/products/${id}`, payload);
        alert('Product updated successfully!');
      } else {
        await api.post('/seller/products', payload);
        alert('Product created successfully!');
      }
      navigate('/products');
    } catch (error: any) {
      console.error(`Failed to ${isEditMode ? 'update' : 'create'} product:`, error);
      const errorMessage = error.response?.data?.message || error.response?.data?.errors 
        ? JSON.stringify(error.response.data.errors || error.response.data.message)
        : `Failed to ${isEditMode ? 'update' : 'create'} product`;
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <button
          type="button"
          onClick={() => navigate('/products')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft size={20} className="text-gray-600" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {isEditMode ? t('titleEdit', { defaultValue: 'Edit Product' }) : t('title')}
          </h1>
          <p className="text-gray-600 mt-2">
            {isEditMode
              ? t('subtitleEdit', { defaultValue: 'Update product information' })
              : t('subtitle')}
          </p>
        </div>
      </div>

      {/* Form */}
      {loadingProduct ? (
        <div className="card p-8 flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-custom-primary"></div>
        </div>
      ) : (
        <div className="card p-8">
          <form onSubmit={handleSubmit} className="space-y-8">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-6">
              {t('sections.basicInfo', { defaultValue: 'Basic Information' })}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              {/* Product Name */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Product Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input-field"
                  placeholder="Enter product name"
                  required
                />
              </div>

              {/* Brand */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Brand *
                </label>
                <select
                  value={formData.brand}
                  onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                  className="input-field"
                  required
                >
                  <option value="">Select a brand</option>
                  {brands.map((brand) => (
                    <option key={brand.id} value={brand.name}>
                      {brand.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category *
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="input-field"
                  required
                >
                  <option value="">Select a category</option>
                  {getSelectableCategories().map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {formatCategoryName(cat)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Product Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('fields.productType')}
                </label>
                <select
                  value={formData.productType}
                  onChange={(e) => handleProductTypeChange(e.target.value as 'simple' | 'variable')}
                  className="input-field"
                >
                  <option value="simple">{t('productType.simple')}</option>
                  <option value="variable">{t('productType.variable')}</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">{t('productType.hint')}</p>
              </div>
            </div>
          </div>

          {/* Product Images */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Product Images</h3>
            <p className="text-gray-600 text-sm mb-6">Upload up to 4 images. The first image will be used as the cover image.</p>
            
            {/* Image Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              {/* Existing Images */}
              {formData.images.map((url, index) => (
                <div key={index} className="relative group">
                  <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden border-2 border-gray-200">
                    <img
                      src={url}
                      alt={`Product ${index + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        target.nextElementSibling?.classList.remove('hidden');
                      }}
                    />
                    <div className="hidden absolute inset-0 flex items-center justify-center bg-gray-100 rounded-lg">
                      <ImageIcon size={24} className="text-gray-400" />
                    </div>
                  </div>
                  
                  {/* Cover Label for First Image */}
                  {index === 0 && (
                    <div className="absolute bottom-2 left-2 bg-white px-2 py-1 rounded text-xs font-medium text-gray-700 shadow">
                      Cover
                    </div>
                  )}
                  
                  {/* Delete Button */}
                  <button
                    type="button"
                    onClick={() => handleImageRemove(index)}
                    className="absolute -top-2 -right-2 bg-black text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-gray-800 transition-colors"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
              
              {/* Add Photo Slot - Only show one at a time */}
              {formData.images.length < 4 && (
                <div className="relative">
                  <div 
                    className={`aspect-square bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 hover:border-gray-400 transition-colors cursor-pointer flex flex-col items-center justify-center ${
                      dragActive ? 'border-custom-primary bg-custom-primary/10' : ''
                    }`}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                    onClick={() => document.getElementById('imageInput')?.click()}
                  >
                    {uploadingImage ? (
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-custom-primary"></div>
                    ) : (
                      <>
                        <div className="w-8 h-8 mb-2 flex items-center justify-center">
                          <Upload size={24} className="text-gray-400" />
                        </div>
                        <span className="text-sm text-gray-500 font-medium">Add Photo</span>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Hidden File Input */}
            <input
              id="imageInput"
              type="file"
              accept="image/*"
              onChange={handleFileInputChange}
              className="hidden"
              disabled={uploadingImage}
            />

            {/* Info Text */}
            <div className="flex items-start space-x-3 text-sm text-gray-600">
              <ImageIcon size={20} className="text-gray-400 mt-0.5 flex-shrink-0" />
              <p>
                Supported formats: JPEG, PNG, GIF, WebP. Max file size: 10MB. Images are automatically compressed for faster upload.
              </p>
            </div>
          </div>

          {/* Description */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Description</h3>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="input-field w-full"
              rows={6}
              placeholder="Enter product description"
              required
            />
          </div>

          {/* Pricing & Inventory */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Pricing & Inventory</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Price */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Price
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  className="input-field"
                  placeholder="0.00"
                  required
                />
              </div>

              {/* Original Price */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Original Price (Optional)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.originalPrice}
                  onChange={(e) => setFormData({ ...formData, originalPrice: e.target.value })}
                  className="input-field"
                  placeholder="0.00"
                />
              </div>

              {formData.productType === 'simple' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Quantity Available
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.quantityAvailable}
                    onChange={(e) => setFormData({ ...formData, quantityAvailable: e.target.value })}
                    className="input-field"
                    placeholder="0"
                    required
                  />
                </div>
              )}

              {/* Condition */}
              {formData.productType === 'simple' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('fields.condition')} *
                  </label>
                  <select
                    value={formData.condition}
                    onChange={(e) => setFormData({ ...formData, condition: e.target.value as 'Brand New' | 'Used' | 'Refurbished' })}
                    className="input-field"
                    required
                  >
                    <option value="Brand New">{t('condition.Brand New')}</option>
                    <option value="Used">{t('condition.Used')}</option>
                    <option value="Refurbished">{t('condition.Refurbished')}</option>
                  </select>
                </div>
              )}

              {formData.productType === 'simple' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Color (Optional)
                  </label>
                  <input
                    type="text"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="input-field"
                    placeholder="e.g., Red, Blue, Black"
                  />
                </div>
              )}
            </div>
          </div>

          <ProductVariantEditor
            enabled={formData.productType === 'variable'}
            config={variantConfig}
            variants={variantRows}
            basePrice={parseFloat(formData.price) || 0}
            baseOriginalPrice={
              formData.originalPrice ? parseFloat(formData.originalPrice) : parseFloat(formData.price) || 0
            }
            onConfigChange={setVariantConfig}
            onVariantsChange={setVariantRows}
            onRequestEnable={enableVariableProduct}
          />

          {/* Product Status */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Product Status</h3>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="h-4 w-4 text-custom-primary focus:ring-custom-primary border-gray-300 rounded"
              />
              <label htmlFor="isActive" className="text-sm text-gray-700">
                Product is active (visible to customers)
              </label>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-4 pt-6 border-t">
            <button
              type="button"
              onClick={() => navigate('/products')}
              className="btn-outline"
            >
              Cancel
            </button>
                  <button
                    type="submit"
                    disabled={loading || uploadingImage || loadingProduct}
                    className="btn-primary disabled:opacity-50"
                  >
                    {loading ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Product' : 'Create Product')}
                  </button>
          </div>
        </form>
      </div>
      )}
    </div>
  );
};

export default AddProduct;

